import * as benepiaApi from '@apis/benepia';
import { useAuthentication } from '@hooks/authentication';
import { useQuery } from '@tanstack/react-query';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { Button, DatePicker, DatePickerProps, Input, Modal, Select, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export const BenepiaUsersPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [editUserId, setEditUserId] = useState<string>();
  const [editUserData, setEditUserData] = useState<Partial<benepiaApi.BenepiaUser>>();
  const [excelLoading, setExcelLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<benepiaApi.SearchBenepiaUsersQuery>({
    page: 0,
    size: 20,
    sort: 'createdAt',
    startDate: undefined,
    endDate: undefined,
  });

  useAuthentication();

  const userSearchQuery = useQuery({
    queryKey: [`benepia-search-users`, JSON.stringify(searchQuery)],
    queryFn: () => benepiaApi.searchBenepiaUsers(searchQuery),
  });

  const userQuery = useQuery({
    queryKey: [`benepia-user-item-${editUserId}`],
    queryFn: () => benepiaApi.getBenepiaUser(editUserId!),
    enabled: !!editUserId,
  });

  useEffect(() => {
    if (userQuery.data?.data) {
      setEditUserData(userQuery.data.data);
    }
  }, [userQuery.data?.data]);

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    const dateString = localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), {
      timeToZero: true,
    });
    setSearchQuery({
      ...searchQuery,
      startDate: dateString || undefined,
    });
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    const dateString = localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true });
    setSearchQuery({
      ...searchQuery,
      endDate: dateString || undefined,
    });
  };

  const handleUpdateUser = async () => {
    if (!editUserId || !editUserData) return;

    try {
      await benepiaApi.updateBenepiaUser(editUserId, editUserData);
      toast.success('유저 정보를 수정했습니다.');
      setEditUserId(undefined);
      setEditUserData(undefined);
      userSearchQuery.refetch();
    } catch (error) {
      toast.error('유저 정보 수정에 실패했습니다.');
    }
  };

  // const handleDeleteUser = async (userId: string) => {
  //   try {
  //     await benepiaApi.deleteBenepiaUser(userId);
  //     toast.success('유저를 삭제했습니다.');
  //     userSearchQuery.refetch();
  //   } catch (error) {
  //     toast.error('유저 삭제에 실패했습니다.');
  //   }
  // };

  const TABLE_COLUMNS: ColumnsType<benepiaApi.BenepiaUser> = [
    {
      title: 'ID',
      align: 'center',
      width: '140px',
      dataIndex: '_id',
      render: (_id) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('사용자 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
              });
            }}>
            ID 복사
          </Button>
        </div>
      ),
    },
    // {
    //   title: '수정',
    //   align: 'center',
    //   width: '80px',
    //   render: (record) => {
    //     const _id = record._id;
    //     return (
    //       <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
    //         <Button
    //           onClick={() => {
    //             setEditUserId(_id);
    //           }}>
    //           수정
    //         </Button>
    //         {!record.deletedAt && (
    //           <Button
    //             type="dashed"
    //             danger
    //             onClick={() => {
    //               if (window.confirm('정말 삭제하시겠습니까?')) {
    //                 handleDeleteUser(_id);
    //               }
    //             }}>
    //             삭제
    //           </Button>
    //         )}
    //       </div>
    //     );
    //   },
    // },
    {
      title: '베네피아 ID',
      dataIndex: 'benefit_id',
      align: 'center',
      width: '120px',
    },
    {
      title: '전화번호',
      dataIndex: 'phone',
      align: 'center',
      width: '136px',
      render: (phone) => {
        if (!phone) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(phone);
              toast.info('전화번호를 클립보드에 복사했습니다.');
            }}>
            {phone}
          </Button>
        );
      },
    },
    {
      title: '사이트코드',
      dataIndex: 'sitecode',
      align: 'center',
      width: '100px',
      render: (sitecode) => sitecode || '-',
    },
    {
      title: '사이트명',
      dataIndex: 'sitename',
      align: 'center',
      width: '120px',
      render: (sitename) => sitename || '-',
    },
    {
      title: '개인정보 동의',
      dataIndex: 'privacyPolicyAgree',
      align: 'center',
      width: '100px',
      render: (agree) => (agree ? '⭕' : 'X'),
    },
    {
      title: '마케팅 동의',
      dataIndex: 'marketingAgree',
      align: 'center',
      width: '100px',
      render: (agree) => (agree ? '⭕' : 'X'),
    },
    {
      title: '가입일',
      width: '120px',
      align: 'center',
      render: (record) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY.MM.DD HH:mm:ss')}</div>
            {record.deletedAt && (
              <div style={{ color: 'red' }}>{moment(record.deletedAt).format('YYYY.MM.DD HH:mm:ss')}</div>
            )}
          </div>
        );
      },
    },
  ];

  const searchResult = userSearchQuery.data?.data;

  const getExcelFile = async () => {
    setExcelLoading(true);
    try {
      const fileName = `igogo-benepia-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const blob = await benepiaApi.getSearchBenepiaUsersExcelFile(searchQuery);
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } catch (error) {
      toast.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <Modal
        open={!!editUserId}
        title="베네피아 유저 수정"
        onCancel={() => {
          setEditUserId(undefined);
          setEditUserData(undefined);
        }}
        onOk={handleUpdateUser}
        okText="저장"
        cancelText="취소">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
          <div>
            <label>이름</label>
            <Input
              value={editUserData?.name || ''}
              onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
            />
          </div>
          <div>
            <label>전화번호</label>
            <Input
              value={editUserData?.phone || ''}
              onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
            />
          </div>
          <div>
            <label>개인정보 동의</label>
            <Select
              value={editUserData?.privacyPolicyAgree ? 'true' : 'false'}
              style={{ width: '100%' }}
              onChange={(value) => setEditUserData({ ...editUserData, privacyPolicyAgree: value === 'true' })}
              options={[
                { value: 'true', label: '동의' },
                { value: 'false', label: '미동의' },
              ]}
            />
          </div>
          <div>
            <label>마케팅 동의</label>
            <Select
              value={editUserData?.marketingAgree ? 'true' : 'false'}
              style={{ width: '100%' }}
              onChange={(value) => setEditUserData({ ...editUserData, marketingAgree: value === 'true' })}
              options={[
                { value: 'true', label: '동의' },
                { value: 'false', label: '미동의' },
              ]}
            />
          </div>
        </div>
      </Modal>

      <div className="page-header">
        <div>베네피아 유저 관리</div>
        <div style={{ display: 'flex' }}>
          <div style={{ marginLeft: '10px' }}>
            <DatePicker
              onChange={onChangeStartDate}
              value={searchQuery.startDate ? dayjs(utcTimeToLocalISOString(searchQuery.startDate as any)) : null}
            />
          </div>
          <div style={{ marginLeft: '8px' }}>
            <DatePicker
              onChange={onChangeEndDate}
              value={searchQuery.endDate ? dayjs(utcTimeToLocalISOString(searchQuery.endDate as any)) : null}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <FilterSelect
              inactiveValue="createdAt"
              value={searchQuery.sort || 'createdAt'}
              style={{ width: 180 }}
              onChange={(value) => {
                setSearchQuery({ ...searchQuery, sort: value });
              }}
              options={[
                { value: 'createdAt', label: '최근 가입일 순' },
                { value: 'updatedAt', label: '최근 업데이트 순' },
              ]}
            />
            <Input
              className={filterActiveClassName(!!searchQuery.keyword)}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  setSearchQuery({ ...searchQuery, keyword: searchKeyword });
                }
              }}></Input>
            <Button
              onClick={() => {
                setSearchKeyword(undefined);
                setSearchQuery({
                  page: 0,
                  size: 20,
                  sort: 'createdAt',
                  startDate: undefined,
                  endDate: undefined,
                });
              }}>
              검색 초기화
            </Button>
            <div style={{ fontWeight: 'bold' }}>총 검색결과: {searchResult?.total || 0}</div>
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                style={{ marginLeft: '10px' }}
                loading={excelLoading}
                onClick={() => {
                  getExcelFile();
                }}>
                검색결과 엑셀 파일 다운로드
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={searchResult?.contents || []}
            pagination={{
              current: (searchResult?.page || 0) + 1,
              pageSize: searchResult?.size || 20,
              total: searchResult?.total || 0,
            }}
            loading={userSearchQuery.isLoading}
            onChange={(e) => {
              setSearchQuery({ ...searchQuery, page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BenepiaUsersPage;
